import styled from '@emotion/styled';
import { useFlags } from 'launchdarkly-react-client-sdk';
import React, { useState } from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { useLocation } from 'react-router';

import { navigationService } from 'src/config/rootService';
import { Dispatch, RootState } from 'src/config/rootStore';
import logoImg from 'src/public/img/mh-logo-text.svg';
import { Input } from 'src/shared/components/Input';
import { ButtonGroup } from 'src/shared/ds';
import { Button } from 'src/shared/ds/components/Button';
import { Input as NewInput, InputType } from 'src/shared/ds/components/Input';
import { PasswordInput } from 'src/shared/ds/components/PasswordInput';
import { hexToRgba } from 'src/shared/helpers/hexToRgba';
import { EapAuthLayout } from 'src/shared/layout/EapAuthLayout';
import { color, font } from 'src/shared/styles/variables';

import { AuthorizationBox } from '../components/AuthorizationBox';
import {
    getAuthenticationErrorMessage,
    isLoginInProgress,
} from '../selectors/authenticationSelectors';
import * as S from './styles';

interface RegistrationErrorProps {
    displayError: boolean;
}
type Props = WithTranslation &
    ReturnType<typeof mapStateToProps> &
    ReturnType<typeof mapDispatchToProps>;
const ErrorContainer = styled.div<RegistrationErrorProps>(({ displayError }) => ({
    display: displayError ? 'inline' : 'none',
    borderRadius: '4px',
    backgroundColor: `${hexToRgba(color.cornflowerBlueDark, 0.1)}`,
    width: '100%',
}));
const ErrorMessage = styled.div(
    {
        color: `${color.blueBayoux}`,
        lineHeight: '24px',
        padding: '12px',
    },
    font.roman(font.size.medium),
);
const BrandImage = styled.img({
    marginLeft: 32,
    marginTop: 20,
    width: '15em',
    cursor: 'pointer',
    marginRight: 12,
});
const Header = styled.div<RegistrationErrorProps>(
    ({ displayError }) => ({
        color: color.ebonyClay,
        width: '100%',
        textAlign: 'left',
        paddingBottom: displayError ? '16px' : 0,
    }),
    font.heavy(font.size.extraLarge),
);
const ButtonsContainer = styled.div({
    width: '100%',
    alignItems: 'start',
    paddingTop: '24px',
    paddingBottom: '12px',
});
const PasswordResetButton = styled.button(
    {
        paddingTop: '24px',
        textAlign: 'left',
        width: '100%',
        '&&': {
            margin: 0,
            backgroundColor: 'transparent',
            boxShadow: 'none',
            border: 'none',
            color: color.royalBlue,
        },
    },
    font.heavy(font.size.base),
);
const SSOButton = styled.div({
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
});
const FormContainer = styled.div({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    maxWidth: '500px',
    flexGrow: 1,
});
const InputContainer = styled.div({
    width: '100%',
    paddingTop: '8px',
    '& > *': {
        paddingTop: '16px',
    },
});
const Container = styled.div({
    display: 'flex',
    alignItems: 'start',
    paddingTop: '112px',
    justifyContent: 'center',
    flexGrow: 1,
});
export enum LoginFormTestId {
    EmailField = 'LoginFormTestId.EmailField',
    OldEmailField = 'LoginFormTestId.OldEmailField',
    PasswordField = 'LoginFormTestId.PasswordField',
    OldPasswordField = 'LoginFormTestId.OldPasswordField',
    ContinueButton = 'LoginFormTestId.ContinueButton',
    BackButton = 'LoginFormTestId.BackButton',
    SSOButton = 'LoginFormTestId.SSOButton',
    ForgotPasswordButton = 'LoginFormTestId.ForgotPasswordButton',
    ErrorMessage = 'LoginFormTestId.ErrorMessage',
}
interface LocationProps {
    existingAccount: boolean;
}

const LoginForm: React.FC<Props> = ({ t, loginInProgress, login, errorMessage }) => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const location = useLocation<LocationProps>();
    const existingAccount = location.state?.existingAccount;
    const displayError = errorMessage || existingAccount ? true : false;

    const handleSubmit = async () => {
        if (!loginInProgress) {
            login({
                email: email.toLowerCase(),
                password,
            });
        }
    };
    const { useRegistrationRevamp } = useFlags();

    return useRegistrationRevamp ? (
        <>
            <BrandImage
                src={logoImg}
                onClick={() => {
                    window.location.href = 'https://www.modernhealth.com';
                }}
            />
            <Container>
                <FormContainer>
                    <Header displayError={displayError}>{t('auth:login_form.title')}</Header>
                    <ErrorContainer
                        displayError={displayError}
                        data-testid={LoginFormTestId.ErrorMessage}
                    >
                        {errorMessage ? (
                            <ErrorMessage>{t(errorMessage)}</ErrorMessage>
                        ) : (
                            <ErrorMessage>{t('auth:login_form.already_have_account')}</ErrorMessage>
                        )}
                    </ErrorContainer>
                    <InputContainer>
                        <NewInput
                            fieldId="email"
                            label={t('auth:common_form_inputs.labels.email')}
                            type={InputType.TextField}
                            value={email}
                            onChange={value => setEmail(value.toString().trim())}
                            testID={LoginFormTestId.EmailField}
                        />
                        <PasswordInput
                            fieldId="password"
                            label={t('auth:common_form_inputs.labels.password')}
                            placeholder={t('auth:common_form_inputs.labels.password')}
                            value={password}
                            onChange={value => setPassword(value.toString().trim())}
                            testID={LoginFormTestId.PasswordField}
                        />
                    </InputContainer>
                    <PasswordResetButton
                        onClick={() => navigationService.navigate('/password-reset')}
                        data-testid={LoginFormTestId.ForgotPasswordButton}
                    >
                        {t('auth:authorization_box.forgot_your_password')}
                    </PasswordResetButton>
                    {/* Instead of having 2 button components, we want to extend the buttonGroup component
                    this will be done in https://joinmodernhealth.atlassian.net/browse/FOUNDATION-306*/}
                    <ButtonsContainer>
                        <ButtonGroup
                            firstButtonText={t('auth:login_form.back')}
                            secondButtonText={t('auth:login_form.continue')}
                            onFirstButtonClick={() => navigationService.navigate('/')}
                            onSecondButtonClick={handleSubmit}
                        />
                    </ButtonsContainer>
                    <SSOButton>
                        <Button
                            onClick={() => navigationService.navigate('/single-sign-on')}
                            title={t('auth:login_form.log_in_using_single_sign_on')}
                            variant="flat"
                            testID={LoginFormTestId.SSOButton}
                            size="large"
                        />
                    </SSOButton>
                </FormContainer>
            </Container>
        </>
    ) : (
        <EapAuthLayout>
            <AuthorizationBox
                title={t('auth:login_form.welcome_back')}
                buttonText={t('auth:login_form.log_in')}
                questionText={t('auth:login_form.dont_have_an_account')}
                needHelp={t('auth:login_form.need_help')}
                questionButtonText={t('auth:login_form.register')}
                isFetching={loginInProgress}
                onSingleSignOnRequest={() => navigationService.navigate('/single-sign-on')}
                onPasswordResetRequest={() => navigationService.navigate('/password-reset')}
                onModeChange={() => navigationService.navigate('/')}
                onSubmit={handleSubmit}
            >
                <>
                    <Input
                        fieldId="email"
                        label={t('auth:common_form_inputs.labels.email')}
                        type="text"
                        value={email}
                        onChange={(text: string) => setEmail(text.trim())}
                        testID={LoginFormTestId.OldEmailField}
                    />
                    <Input
                        fieldId="password"
                        label={t('auth:common_form_inputs.labels.password')}
                        type="password"
                        value={password}
                        onChange={(text: string) => setPassword(text.trim())}
                        testID={LoginFormTestId.OldPasswordField}
                    />
                    {errorMessage && <S.InvalidCredentials>{t(errorMessage)}</S.InvalidCredentials>}
                </>
            </AuthorizationBox>
        </EapAuthLayout>
    );
};

const mapStateToProps = (state: RootState) => ({
    errorMessage: getAuthenticationErrorMessage(state),
    loginInProgress: isLoginInProgress(state),
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
    login: dispatch.authentication.login,
});

export default withTranslation()(connect(mapStateToProps, mapDispatchToProps)(LoginForm));
